// src/components/result/ActivityCardFromFeedItem.js
import React, { useMemo } from "react";
import { ExertionResultPreview } from "./ExertionResultPreview";
import { SpirometryResultPreview } from "./SpirometryResultPreview";
// Centralised fetch here:
import { useActivityData } from "../../hooks/useActivityData";
import { useTranslation } from "react-i18next";
import { formatDateLong, formatTime } from "../../utils/time";
import { ResultCard } from "./ResultCard";
import { Link } from "react-router-dom";

export default function ActivityCardFromFeedItem({ item, locked }) {
  const { t } = useTranslation();

  const date = item?.date ?? new Date();
  const hasResults = item?.status === "ready";
  const live = item?.metadata || null; // ← from feed
  const isDelayed = Boolean(item?.flags?.delayed);
  const hasFailed = Boolean(item?.flags?.failed);

  // Fetch the activity JSON for this feed item (spirometry or exertion)
  const { loading, data } = useActivityData({
    uid: item?.uid || null,
    baseName: item?.baseName || item?.id || null,
    tag: "breatheultra",
    fetchBlob: true,
    // Optional: force re-check when Firestore content changes
    refreshKey: item?.sig || item?.timestamp,
  });

  // Prefer Firestore live doc; fall back to blob (if/when it’s fetched)
  const mergedData = live ?? data ?? null;
  const mergedLoading = !hasResults ? true : live ? false : loading;

  const titleKey =
    item?.kind === "exertion"
      ? "activity.exertion-recovery.exertion.title"
      : "activity.spirometry.title";
  const titleDefault =
    item?.kind === "exertion" ? "Exertion" : "Lung Fitness & Age Test";

  const subtitle = useMemo(() => {
    const d = date instanceof Date ? date : new Date(date);
    return `${formatDateLong(d)} · ${formatTime(d)}`;
  }, [date]);

  // --- 1) Delayed warning state
  if (isDelayed || hasFailed) {
    return (
      <ResultCard
        title={t(titleKey, { defaultValue: titleDefault })}
        subtitle={subtitle}
      >
        <p className="">
          {t("activity.delayedMessage", {
            defaultValue:
              "Processing is taking longer than expected, or there was an issue with your recording.",
          })}
        </p>
        <Link
          to="/info"
          className="flex flex-row justify-between items-center text-primary hover:italic focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-md"
          aria-label={t("settings.infoAria", {
            defaultValue: "View information about your metrics",
          })}
        >
          {t("activity.seeInfo", {
            defaultValue: "See our info page for more",
          })}
          <span className="material-symbols-outlined text-[18px]" aria-hidden>
            chevron_right
          </span>
        </Link>
      </ResultCard>
    );
  }

  if (item?.kind === "exertion") {
    return (
      <ExertionResultPreview
        activityId={item.baseName || item.id}
        date={date}
        data={hasResults ? mergedData : null}
        loading={mergedLoading}
      />
    );
  }

  if (item?.kind === "spirometry") {
    return (
      <SpirometryResultPreview
        activityId={item.baseName || item.id}
        date={date}
        data={hasResults ? mergedData : null}
        loading={mergedLoading}
      />
    );
  }

  return null;
}
